import { useAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import type { FunctionComponent, ReactNode } from "react";
import { Transition } from "react-transition-group";

import { hamburgerMenuAtom } from "~/components/hamburger-menu/atoms";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { useEditionValue } from "~/lib/edition";
import { useDocumentBodyScrollLock } from "~/lib/hooks";

import { Container, Layer, MenuWrapper } from "./styles";

type ChildrenProps = {
  closeClickedHandler: () => void;
  closeMenu: () => void;
};

type Props = {
  backdrop?: boolean;
  children?: (props: ChildrenProps) => ReactNode;
};
export const HamburgerMenuWrapper: FunctionComponent<Props> = ({ backdrop, children }) => {
  const [hamburgerMenu, setHamburgerMenuState] = useAtom(hamburgerMenuAtom);
  const isHamburgerMenuOpen = hamburgerMenu.isOpen;
  const nodeReference = useRef(null);
  const { lockDocumentBodyScroll } = useDocumentBodyScrollLock();
  const edition = useEditionValue();

  useEffect(() => {
    lockDocumentBodyScroll(isHamburgerMenuOpen);
  }, [isHamburgerMenuOpen, lockDocumentBodyScroll]);

  const closeMenu = useCallback(() => {
    setHamburgerMenuState(current => ({ ...current, isOpen: false }));
  }, [setHamburgerMenuState]);

  const closeClickedHandler = useCallback(() => {
    closeMenu();
    sendGATracking({
      action: "Menu/Close",
      category: "Menu",
      label: "Menu/Close",
    });
    sendGA4Tracking({
      action: "click",
      category: "menu",
      customized_parameters: {
        action_type: "close",
        edition,
        menu_type: "hamburger",
      },
      subcategory: "section",
    });
  }, [closeMenu, edition]);

  const childrenProps = { closeClickedHandler, closeMenu };

  return (
    <Transition in={isHamburgerMenuOpen} nodeRef={nodeReference} timeout={200} appear mountOnEnter>
      {state => (
        <Container
          $backdrop={backdrop}
          $state={state}
          $variant={hamburgerMenu.variant}
          ref={nodeReference}
        >
          <Layer onClick={closeClickedHandler} />
          <MenuWrapper>{children?.(childrenProps)}</MenuWrapper>
        </Container>
      )}
    </Transition>
  );
};
